import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';

import { errorFormat } from 'src/common/errorFormatting';
import { SuccessBag } from 'src/common/icons/successBag';
import {
  getLabelPackage,
  getPackageLabelRange,
  mergeWarnings,
  MultiLabelPackage,
  parsePackageRange,
  PrinterComputer,
  PrintQueue,
  printRetailBoxLabels,
  PrintRetailBoxLabelsResult,
  RetailBoxLabelsBlock,
  RetailLabelsPrintError,
  RetailLabelsPrinter,
  RetailLabelsProgress,
  RetailLabelsRequest,
} from 'src/common/picking/logic/retailLabels';
import { playErrorSound } from 'src/common/picking/logic/sounds';
import { CircularProgress } from 'src/components/atoms/circularProgress';
import {
  ActionButton,
  ButtonsContainer,
  Container,
  ErrorText,
  Icon,
  RangeInput,
  ReprintLabel,
  ReprintRow,
  StatusRow,
  StatusText,
  Title,
  WarningList,
} from './styles';

export interface RetailBoxLabelsProps {
  order: string;
  boxCount: number | null;
  packageCount: number | null;
  multiLabelPackages: MultiLabelPackage[] | null;
  autoPrint: boolean;
  fetchLabels: (request: RetailLabelsRequest) => Promise<RetailBoxLabelsBlock>;
  resolvePrinter: (order: string) => Promise<RetailLabelsPrinter>;
  loading: boolean;
  onNextPicking?: () => void;
  onBack: () => void;
}

interface Notice {
  kind: 'info' | 'error';
  text: string;
}

const DIGITS_ONLY_REGEX = /^\d*$/;
const SCANNED_CHARACTER_REGEX = /^[A-Za-z0-9]$/;

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');

const blurFocusedElement = () => {
  const { activeElement } = document;
  if (activeElement instanceof HTMLElement && activeElement !== document.body && !isEditableTarget(activeElement)) {
    activeElement.blur();
  }
};

const blurButton = (event: React.MouseEvent<HTMLElement>) => {
  event.currentTarget.blur();
};

const NO_ZEBRA_HERE = 'Sem Zebra nesta estação.';

const countText = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

const boxesAndLabelsText = (labelCount: number | null, packageCount: number | null) => {
  if (labelCount === null) {
    return '';
  }
  if (packageCount === null || packageCount >= labelCount) {
    return ` com ${countText(labelCount, 'caixa', 'caixas')}`;
  }
  return ` com ${countText(packageCount, 'caixa', 'caixas')} e ${countText(labelCount, 'etiqueta', 'etiquetas')}`;
};

const viaPrinterComputer = (printer: RetailLabelsPrinter | null): printer is PrinterComputer =>
  Boolean(printer && printer.kind === 'printerComputer');

const viaPrintQueue = (printer: RetailLabelsPrinter | null): printer is PrintQueue =>
  Boolean(printer && printer.kind === 'printQueue');

const destination = (printer: RetailLabelsPrinter | null) => {
  if (viaPrinterComputer(printer)) {
    return `ao computador impressor nº ${printer.pcId}`;
  }
  return viaPrintQueue(printer) ? `à impressora do computador nº ${printer.pcId}` : 'à impressora';
};

const sentProgress = (progress: RetailLabelsProgress | null, printFrom: number) =>
  progress ? ` Etiquetas ${printFrom} a ${progress.to} de ${progress.total} já enviadas.` : '';

const printingText = (printer: RetailLabelsPrinter | null, progress: RetailLabelsProgress | null, printFrom: number) => {
  if (viaPrinterComputer(printer)) {
    return `${NO_ZEBRA_HERE} Enviando as etiquetas ao computador impressor nº ${printer.pcId}...${sentProgress(
      progress,
      printFrom
    )}`;
  }
  if (viaPrintQueue(printer)) {
    return `Enviando as etiquetas à impressora do computador nº ${printer.pcId}...${sentProgress(progress, printFrom)}`;
  }
  return progress
    ? `Imprimindo etiquetas ${progress.from} a ${progress.to} de ${progress.total}...`
    : 'Preparando as etiquetas...';
};

const resumeText = (action: string, multiLabelPackages: MultiLabelPackage[], lastPrinted: number) => {
  const resumePackage = getLabelPackage(multiLabelPackages, lastPrinted + 1);
  const repeated =
    getPackageLabelRange(multiLabelPackages, resumePackage, resumePackage).from <= lastPrinted
      ? ` O volume ${resumePackage} sai inteiro de novo: descarte as etiquetas repetidas.`
      : '';
  return `${action} a partir do volume ${resumePackage}.${repeated}`;
};

const stoppedText = (
  printer: RetailLabelsPrinter | null,
  from: number,
  result: PrintRetailBoxLabelsResult,
  multiLabelPackages: MultiLabelPackage[]
) => {
  if (viaPrinterComputer(printer)) {
    return result.printed > 0
      ? `Envio parado. As etiquetas ${from} a ${result.lastPrinted} de ${
          result.total
        } já foram ao computador impressor nº ${printer.pcId} e saem lá. ${resumeText(
          'Reenvie',
          multiLabelPackages,
          result.lastPrinted
        )}`
      : 'Envio parado antes da primeira etiqueta.';
  }
  if (viaPrintQueue(printer)) {
    return result.printed > 0
      ? `Envio parado. As etiquetas ${from} a ${result.lastPrinted} de ${
          result.total
        } já foram enviadas à impressora do computador nº ${printer.pcId}. ${resumeText(
          'Reenvie',
          multiLabelPackages,
          result.lastPrinted
        )}`
      : 'Envio parado antes da primeira etiqueta.';
  }
  return result.printed > 0
    ? `Impressão parada. Última etiqueta enviada: ${result.lastPrinted} de ${result.total}. ${resumeText(
        'Reimprima',
        multiLabelPackages,
        result.lastPrinted
      )}`
    : 'Impressão parada antes da primeira etiqueta.';
};

const doneText = (printer: RetailLabelsPrinter | null, from: number, result: PrintRetailBoxLabelsResult) => {
  const sent = `${result.printed} etiquetas enviadas ${destination(printer)} (${from} a ${result.lastPrinted} de ${
    result.total
  }). Confira a ordem das caixas antes de fechar.`;
  return viaPrinterComputer(printer) ? `${NO_ZEBRA_HERE} ${sent}` : sent;
};

const failedText = (
  printer: RetailLabelsPrinter | null,
  from: number,
  printError: RetailLabelsPrintError | null,
  message: string
) => {
  const where =
    printError && printError.printed > 0
      ? `depois da etiqueta ${printError.lastPrinted} de ${printError.total}`
      : `antes da etiqueta ${from}`;
  if (viaPrinterComputer(printer)) {
    const repeated =
      printError && printError.printed > 0 ? ' Se lá saíram etiquetas a mais, descarte as repetidas.' : '';
    return `${NO_ZEBRA_HERE} O envio ao computador impressor nº ${printer.pcId} parou ${where}. ${message}${repeated}`;
  }
  if (viaPrintQueue(printer)) {
    const repeated = printError && printError.printed > 0 ? ' Se saíram etiquetas a mais, descarte as repetidas.' : '';
    return `O envio à impressora do computador nº ${printer.pcId} parou ${where}. ${message}${repeated}`;
  }
  return `A impressão parou ${where}. ${message}`;
};

export const RetailBoxLabels: React.FC<RetailBoxLabelsProps> = props => {
  const { enqueueSnackbar } = useSnackbar();
  const [order] = useState(props.order.trim());
  const [total, setTotal] = useState<number | null>(props.boxCount);
  const [packageCount, setPackageCount] = useState<number | null>(props.packageCount);
  const [multiLabelPackages, setMultiLabelPackages] = useState<MultiLabelPackage[] | null>(props.multiLabelPackages);
  const [printing, setPrinting] = useState(false);
  const [printer, setPrinter] = useState<RetailLabelsPrinter | null>(null);
  const [progress, setProgress] = useState<RetailLabelsProgress | null>(null);
  const [printFrom, setPrintFrom] = useState(1);
  const [printedOnce, setPrintedOnce] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [fromText, setFromText] = useState('');
  const [toText, setToText] = useState('');
  const cancelledRef = useRef(false);
  const printingRef = useRef(false);
  const mountedRef = useRef(true);

  const showError = (message: string) => {
    playErrorSound();
    enqueueSnackbar(message, { variant: 'error' });
  };

  const knownMultiLabelPackages = multiLabelPackages || [];

  const suggestRange = (lastPrinted: number, totalCount: number) => {
    if (totalCount > 0 && lastPrinted < totalCount) {
      setFromText(String(getLabelPackage(knownMultiLabelPackages, lastPrinted + 1)));
      setToText(String(getLabelPackage(knownMultiLabelPackages, totalCount)));
    }
  };

  const print = async (from: number, to?: number) => {
    if (printingRef.current) {
      return;
    }
    printingRef.current = true;
    cancelledRef.current = false;
    setPrinting(true);
    setPrinter(null);
    setNotice(null);
    setProgress(null);
    setPrintFrom(from);
    let target: RetailLabelsPrinter | null = null;
    try {
      target = await props.resolvePrinter(order);
      if (mountedRef.current) {
        setPrinter(target);
      }
      const result = await printRetailBoxLabels({
        from,
        to,
        printer: target,
        onProgress: currentProgress => {
          if (mountedRef.current) {
            setTotal(currentProgress.total);
            setPackageCount(currentProgress.packageCount);
            setProgress(currentProgress);
            setWarnings(current => mergeWarnings(current, currentProgress.warnings));
          }
        },
        isCancelled: () => cancelledRef.current,
      });
      if (!mountedRef.current) {
        return;
      }
      if (!result.cancelled || result.total > 0) {
        setTotal(result.total);
      }
      setWarnings(current => mergeWarnings(current, result.warnings));
      setPrintedOnce(current => current || result.printed > 0);
      if (result.cancelled) {
        setNotice({ kind: 'info', text: stoppedText(target, from, result, knownMultiLabelPackages) });
        suggestRange(result.lastPrinted, result.total);
      } else if (result.printed > 0) {
        setNotice({ kind: 'info', text: doneText(target, from, result) });
      }
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      const printError = error instanceof RetailLabelsPrintError ? error : null;
      const message = errorFormat(error);
      if (printError && printError.total > 0) {
        setTotal(printError.total);
        setWarnings(current => mergeWarnings(current, printError.warnings));
        setPrintedOnce(current => current || printError.printed > 0);
      }
      setNotice({ kind: 'error', text: failedText(target, from, printError, message) });
      if (printError) {
        suggestRange(printError.lastPrinted, printError.total);
      }
      showError(message);
    } finally {
      printingRef.current = false;
      if (mountedRef.current) {
        setPrinting(false);
        setProgress(null);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    if (props.autoPrint) {
      print(1);
    } else if (total === null) {
      props
        .fetchLabels({ order, countOnly: true })
        .then(block => {
          if (mountedRef.current) {
            setTotal(block.total);
            setPackageCount(typeof block.packageCount === 'number' ? block.packageCount : null);
            setMultiLabelPackages(Array.isArray(block.multiLabelPackages) ? block.multiLabelPackages : null);
          }
        })
        .catch(error => {
          if (mountedRef.current) {
            setNotice({ kind: 'error', text: `Não foi possível buscar as etiquetas. ${errorFormat(error)}` });
            showError(errorFormat(error));
          }
        });
    }
    return () => {
      mountedRef.current = false;
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!isEditableTarget(event.target) && SCANNED_CHARACTER_REGEX.test(event.key)) {
        blurFocusedElement();
      }
    };
    window.addEventListener('keypress', handleKeyPress);
    return () => {
      window.removeEventListener('keypress', handleKeyPress);
    };
  }, []);

  const handlePrintAll = (event: React.MouseEvent<HTMLElement>) => {
    blurButton(event);
    print(1);
  };

  const handlePrintRange = (event: React.MouseEvent<HTMLElement>) => {
    blurButton(event);
    const range = parsePackageRange(fromText, toText, packageCount !== null ? packageCount : total);
    if (range.error) {
      setNotice({ kind: 'error', text: range.error });
      showError(range.error);
      return;
    }
    const labels = getPackageLabelRange(knownMultiLabelPackages, range.from, range.to);
    print(labels.from, labels.to);
  };

  const handleStop = (event: React.MouseEvent<HTMLElement>) => {
    blurButton(event);
    cancelledRef.current = true;
  };

  const handleBack = (event: React.MouseEvent<HTMLElement>) => {
    blurButton(event);
    props.onBack();
  };

  const handleNextPicking = (event: React.MouseEvent<HTMLElement>) => {
    blurButton(event);
    if (props.onNextPicking) {
      props.onNextPicking();
    }
  };

  const handleRangeChange = (setter: (value: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (DIGITS_ONLY_REGEX.test(event.target.value)) {
      setter(event.target.value);
    }
  };

  const handleRangeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  const boxesText = boxesAndLabelsText(total, packageCount);
  const knownBoxes = props.boxCount !== null && props.boxCount > 0;
  const canPrint = (props.autoPrint || total !== null) && !printing && !props.loading && (total !== 0 || knownBoxes);

  const renderStatus = () => {
    if (printing) {
      return (
        <StatusRow>
          <CircularProgress size={24} />
          <StatusText>{printingText(printer, progress, printFrom)}</StatusText>
        </StatusRow>
      );
    }
    if (notice) {
      return notice.kind === 'error' ? <ErrorText>{notice.text}</ErrorText> : <StatusText>{notice.text}</StatusText>;
    }
    if (total === 0) {
      return knownBoxes ? (
        <ErrorText>A API não encontrou as caixas deste pedido. Tente imprimir de novo.</ErrorText>
      ) : (
        <StatusText>Nenhuma caixa foi separada neste pedido, então não há etiqueta para imprimir.</StatusText>
      );
    }
    if (printedOnce) {
      return (
        <StatusText>Etiquetas enviadas {destination(printer)}. Confira a ordem das caixas antes de fechar.</StatusText>
      );
    }
    return <StatusText>Use os botões abaixo para imprimir as etiquetas de expedição.</StatusText>;
  };

  return (
    <Container title="Etiquetas de expedição">
      <Title>
        {props.autoPrint
          ? `O pedido ${order} foi finalizado${boxesText}!`
          : `Reimpressão das etiquetas do pedido ${order}${boxesText}`}
      </Title>
      {props.autoPrint && (
        <Icon>
          <SuccessBag />
        </Icon>
      )}
      {renderStatus()}
      {warnings.length > 0 && (
        <WarningList>
          {warnings.map(warning => (
            <li key={warning}>{warning}</li>
          ))}
        </WarningList>
      )}
      <ReprintRow>
        <ReprintLabel>Reimprimir do volume</ReprintLabel>
        <RangeInput
          variant="outlined"
          label="de"
          value={fromText}
          onChange={handleRangeChange(setFromText)}
          onKeyDown={handleRangeKeyDown}
          inputProps={{ maxLength: 4, inputMode: 'numeric', 'aria-label': 'primeiro volume' }}
        />
        <RangeInput
          variant="outlined"
          label="até"
          value={toText}
          onChange={handleRangeChange(setToText)}
          onKeyDown={handleRangeKeyDown}
          inputProps={{ maxLength: 4, inputMode: 'numeric', 'aria-label': 'último volume' }}
        />
        <ActionButton size="large" variant="outlined" color="primary" onClick={handlePrintRange} disabled={!canPrint}>
          Reimprimir faixa
        </ActionButton>
        <ActionButton size="large" variant="outlined" color="primary" onClick={handlePrintAll} disabled={!canPrint}>
          {printedOnce ? 'Reimprimir todas' : 'Imprimir todas'}
        </ActionButton>
        {printing && (
          <ActionButton size="large" variant="contained" color="primary" onClick={handleStop}>
            {viaPrinterComputer(printer) || viaPrintQueue(printer) ? 'Parar envio' : 'Parar impressão'}
          </ActionButton>
        )}
      </ReprintRow>
      <ButtonsContainer>
        <ActionButton size="large" variant="outlined" color="primary" onClick={handleBack} disabled={printing}>
          Voltar ao menu
        </ActionButton>
        {props.onNextPicking && (
          <ActionButton
            size="large"
            variant="contained"
            color="secondary"
            onClick={handleNextPicking}
            disabled={printing || props.loading}
          >
            Próximo pedido
          </ActionButton>
        )}
      </ButtonsContainer>
    </Container>
  );
};
